<template>
  <div :class="['form-group', shouldShowFixedInput || isViewMode ? 'viewmode' : '']">
    <label :for="name">
      {{ props.label ? t(`labels.${props.label}`) : t(`labels.${props.name}`) }}
    </label>

    <InputText
      v-if="shouldShowFixedInput && selectedOption?.name"
      :value="showName(selectedOption.name)"
      readonly
      :disabled="true"
      class="p-inputtext p-component p-filled my-2 w-full my-2"
    />

    <Select
      v-else
      :id="name"
      :key="locale"
      v-model="internalValue"
      class="w-full"
      :options="processedOptions"
      :filter="props.filter ?? false"
      :showClear="props.showClear ?? false"
      optionLabel="searchLabel"
      :placeholder="
        props.placeholder
          ? t(`placeholder.${props.placeholder}`, props.placeholder)
          : props.placeholder || '&nbsp'
      "
      :class="[
        'w-full',
        {
          'max-w-[260px] truncate': props.compact,
          'dirty-state': (isFieldDirty || differsFromGlobal) && !disabled,
          'error-state': showError,
        },
      ]"
      :disabled="disabled"
      @change="handleChange"
    >
      <template #value="slotProps">
        <!-- Shows selected value -->
        <div v-if="slotProps.value" class="flex align-items-center">
          <div
            :class="props.compact ? 'truncate' : ''"
            :title="props.compact ? showName(slotProps.value.name) : ''"
            :style="props.compact ? 'max-width: 220px;' : ''"
          >
            {{ showName(slotProps.value.name) }}
          </div>
        </div>
        <span v-else>
          {{ slotProps.placeholder }}
        </span>
      </template>
      <template #option="slotProps">
        <div
          :class="props.compact ? 'truncate' : ''"
          :title="props.compact ? showName(slotProps.option.name) : ''"
          :style="props.compact ? 'max-width: 240px;' : ''"
        >
          {{ showName(slotProps.option.name) }}
        </div>
      </template>
    </Select>
    <FormError v-if="showError" :message="errorMessage" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import type { SelectChangeEvent } from 'primevue/select';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import FormError from './errors/FormError.vue';

export interface SelectModel {
  id: string | number;
  name: string;
}

const props = defineProps<{
  name: string;
  placeholder?: string;
  options: (string | SelectModel)[];
  modelValue: string | undefined;
  initialValue: string | undefined;
  isDirty: (dirty: boolean) => void;
  label?: string;
  disabled?: boolean;
  filter?: boolean;
  showClear?: boolean;
  untranslated?: boolean;
  globalDefault?: string;
  singleValueFixed?: boolean;
  autofillSingleValue?: boolean;
  compact?: boolean;
  viewMode?: boolean;
}>();

const { t, locale } = useI18n();
const { errorMessage, meta, validate, setTouched } = useField<string>(props.name, undefined, {
  // do not validate on mount
  validateOnMount: false,
  // do not auto-validate on every model change (we control it)
  validateOnValueUpdate: false,
});

const { submitCount } = useForm();
const showError = computed(() => {
  if (props.disabled) return false;
  if (!errorMessage.value) return false;

  // Show error only:
  // - after user touched the field, or
  // - after the form has been submitted at least once
  return meta.touched || submitCount.value > 0;
});

const showName = (name: string) =>
  !!props.untranslated ? name : t(`enums.${name.toUpperCase().replace(/\s+/g, '_')}`, name);

const shouldShowFixedInput = computed(() => {
  return (
    !!props.singleValueFixed &&
    !props.showClear &&
    processedOptions.value.length === 1 &&
    !!internalValue.value?.id
  );
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | undefined): void;
  (e: 'change', payload: SelectChangeEvent | string | number | undefined): void;
  (e: 'userChange', payload: SelectChangeEvent): void;
}>();

const internalValue = ref<SelectModel | null>(null);
const initialValue = computed<SelectModel | null>(() => {
  const foundOption = props.options.find(
    (obj): obj is SelectModel =>
      typeof obj === 'object' && 'id' in obj && obj.id === props.initialValue,
  );

  if (props.untranslated) {
    return foundOption || null;
  }

  if (foundOption) {
    return foundOption;
  }

  return null;
});

const isFieldDirty = ref(false); // Track dirty state
const isViewMode = computed(() => !!props.viewMode);

const differsFromGlobal = computed(() => {
  if (!props.globalDefault) return false;
  return internalValue.value?.id !== props.globalDefault;
});

// Type Guard to check if an option is of type SelectModel
function isSelectModel(option: string | SelectModel): option is SelectModel {
  return typeof option === 'object' && 'id' in option && 'name' in option;
}

const processedOptions = computed(() => {
  const _ = locale.value; // force dependency on locale
  return props.options.map((option) => {
    const obj = isSelectModel(option) ? option : { id: option, name: option };
    return {
      ...obj,
      // Use translated label for both display & filtering
      searchLabel: showName(obj.name), // e.g. "Mietvertrag"
    };
  });
});

const selectedOption = computed(() => {
  return processedOptions.value.find((opt) => String(opt.id) === String(props.modelValue));
});

function maybeAutofill() {
  if (props.autofillSingleValue && processedOptions.value.length === 1 && !internalValue.value) {
    internalValue.value = processedOptions.value[0] || null;
  }
}

watch([() => props.autofillSingleValue, processedOptions], maybeAutofill, { immediate: true });

watch(
  () => props.options,
  () => {
    maybeAutofill();
  },
  { immediate: false, deep: true },
);

watch(processedOptions, () => {
  maybeAutofill();
});

watch(locale, () => {
  if (internalValue.value && !props?.untranslated) {
    internalValue.value =
      processedOptions.value.find((o) => o.id === internalValue.value?.id) || internalValue.value;
  }
});

watch(internalValue, (newValue) => {
  internalValue.value = processedOptions.value.find((o) => o.id === newValue?.id) || newValue;

  emit('update:modelValue', newValue ? newValue.id : undefined);
  emit('change', newValue ? newValue.id : undefined); // `change` mit dem neuen Wert emittieren
});

// Watch for changes in modelValue and update internalValue
watch(
  () => props.modelValue,
  (newValue) => {
    internalValue.value =
      processedOptions.value.find(
        (obj) => isSelectModel(obj) && String(obj.id) === String(newValue),
      ) || null;
  },
);

watch(
  () => props.options,
  (newOptions) => {
    if (newOptions && Array.isArray(newOptions) && newOptions.length > 0) {
      if (internalValue.value === null) {
        internalValue.value =
          processedOptions.value.find(
            (option) =>
              typeof option === 'object' &&
              'id' in option &&
              String(option.id) === String(props.modelValue),
          ) || null;
      }
    }
  },
  { immediate: true },
);

// Compare the ids of internalValue and initialValue instead of the objects
watch([internalValue, initialValue], () => {
  const internalId = internalValue.value?.id;
  const initialId = props.initialValue;
  const isDirty = internalId != initialId; // != makes undefined & null be the same
  isFieldDirty.value = isDirty;
  props.isDirty(isDirty);
});

onMounted(() => {
  internalValue.value =
    processedOptions.value.find((obj) => isSelectModel(obj) && obj.id === props.modelValue) || null;
});

function handleChange(event: SelectChangeEvent) {
  // mark as touched & validate only on real user interaction
  setTouched(true);
  void validate();

  emit('change', event);
  emit('userChange', event); // Use this in case you only want to subscribe for user changes
}
</script>
